import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../lib/auth";
import { fmtErr } from "../lib/api";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [need2fa, setNeed2fa] = useState(false);
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        await login(email, password, code || undefined);
        toast.success("Bem-vindo!");
      } else {
        await register(email, password, name);
        toast.success("Conta criada!");
      }
      // If there's a pending invite token in localStorage, go accept it
      const pending = localStorage.getItem("sb_pending_invite");
      if (pending) {
        localStorage.removeItem("sb_pending_invite");
        navigate(`/invite/${pending}`);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "2FA_REQUIRED") {
        setNeed2fa(true);
        toast("Digite o código do seu app 2FA", { icon: "🔐" });
      } else {
        toast.error(fmtErr(detail) || err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const loginGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)" }} data-testid="login-page">
      {/* Left */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: "var(--panel)" }}>
        <div className="absolute inset-0 opacity-40" style={{ background: "radial-gradient(circle at 30% 20%, rgba(192,91,255,0.35), transparent 60%), radial-gradient(circle at 70% 80%, rgba(255,122,90,0.3), transparent 55%)" }} />
        <div className="relative z-10 flex flex-col justify-between p-12 w-full">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-lg grad-bg" />
            <div>
              <div className="font-bold text-lg">Social Booster</div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted">Sistema operacional do social media</div>
            </div>
          </div>
          <div className="space-y-5 max-w-lg">
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold">// método completo</div>
            <h1 className="text-5xl font-extrabold leading-[1.02] tracking-tight">
              Do <span className="grad-text">posicionamento</span> à performance.
            </h1>
            <p className="text-[15px] leading-relaxed text-muted max-w-md">
              Wizard de posicionamento em 14 etapas, workspace do cliente com 7 camadas, calendário
              editorial e funil de métricas. Puxa dados do LABrand ou funciona no manual.
            </p>
          </div>
          <div className="flex items-center gap-4 text-xs text-muted font-mono">
            <span>◐ / ☀ / ☾ 3 temas</span>
            <span>·</span>
            <span>pt-BR</span>
            <span>·</span>
            <span>integrado ao LABrand</span>
          </div>
        </div>
      </div>

      {/* Right */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-6">
          <div className="flex items-center justify-between">
            <div className="lg:hidden flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg grad-bg" />
              <div className="font-bold">Social Booster</div>
            </div>
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest grad-text font-bold mb-2">// acesso</div>
            <h2 className="text-3xl font-extrabold">{mode === "login" ? "Entrar" : "Criar conta"}</h2>
            <p className="text-muted text-sm mt-1">{mode === "login" ? "Acesse seu painel de gestão." : "Comece a gerenciar suas marcas."}</p>
          </div>

          <button onClick={loginGoogle} data-testid="google-login" className="w-full card-2 hover-lift py-3 flex items-center justify-center gap-3 font-medium">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2.1 1.5-4.5 2.4-7.2 2.4-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.1 4.1-3.9 5.6l6.2 5.2c-.4.4 6.7-4.9 6.7-14.8 0-1.3-.1-2.3-.7-3.5z"/></svg>
            Entrar com Google
          </button>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
            <span className="text-xs text-muted font-mono">ou</span>
            <div className="flex-1 h-px" style={{ background: "var(--border)" }} />
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="email-form">
            {mode === "register" && (
              <input className="input" placeholder="Nome" value={name} onChange={(e) => setName(e.target.value)} required data-testid="name-input" />
            )}
            <input className="input" type="email" placeholder="E-mail" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" />
            <input className="input" type="password" placeholder="Senha" minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="password-input" />
            {need2fa && mode === "login" && (
              <input
                className="input text-center tracking-widest text-lg"
                placeholder="000000  (código 2FA ou backup)"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                data-testid="totp-code-input"
                autoFocus
              />
            )}
            <button type="submit" disabled={loading} className="btn-grad w-full" data-testid="submit-button">
              {loading ? "Processando…" : mode === "login" ? "Entrar" : "Criar conta"}
            </button>
          </form>

          <div className="text-center text-sm text-muted">
            {mode === "login" ? (
              <>Ainda não tem conta? <button onClick={() => setMode("register")} className="grad-text font-semibold" data-testid="switch-register">Criar uma agora</button></>
            ) : (
              <>Já tem conta? <button onClick={() => setMode("login")} className="grad-text font-semibold" data-testid="switch-login">Entrar</button></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
